import { ChevronDown, ChevronUp, Pencil, Trash2 } from 'lucide-react'
import type { AccessRule } from '@/types/rules'

interface RuleListProps {
  rules: AccessRule[]
  onEdit: (index: number) => void
  onDelete: (index: number) => void
  onMove: (index: number, direction: 'up' | 'down') => void
}

function formatMatch(rule: AccessRule): string {
  if (rule.match.email_domain) return `@${rule.match.email_domain}`
  if (rule.match.email) return rule.match.email
  return '(未設定)'
}

function formatList(items: string[] | undefined): string {
  if (!items || items.length === 0) return '-'
  if (items.includes('*')) return 'すべて'
  return items.join(', ')
}

export default function RuleList({
  rules,
  onEdit,
  onDelete,
  onMove,
}: RuleListProps) {
  if (rules.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        ルールがありません。「ルール追加」ボタンから追加してください。
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              #
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              マッチ条件
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ロール
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              サイト
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              アプリ
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              API
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              備考
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              操作
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rules.map((rule, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                {formatMatch(rule)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {rule.grants.role ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    {rule.grants.role}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatList(rule.grants.sites)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatList(rule.grants.apps)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatList(rule.grants.apis)}
              </td>
              <td
                className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate"
                title={rule.note}
              >
                {rule.note || '-'}
              </td>
              <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                <button
                  onClick={() => onMove(index, 'up')}
                  disabled={index === 0}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                  title="上へ移動"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onMove(index, 'down')}
                  disabled={index === rules.length - 1}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                  title="下へ移動"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onEdit(index)}
                  className="text-primary-600 hover:text-primary-800 p-1 rounded hover:bg-primary-50 transition-colors"
                  title="編集"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onDelete(index)}
                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors ml-1"
                  title="削除"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
